import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  User, 
  Building2, 
  Settings2, 
  Save, 
  Lock, 
  Check, 
  X,
  Calendar,
  Loader2,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTEURS_ACTIVITE = [
  { value: "services", label: "Services" },
  { value: "commerce", label: "Commerce" },
  { value: "artisanat", label: "Artisanat" },
  { value: "liberal", label: "Profession libérale" },
  { value: "conseil", label: "Conseil" },
  { value: "informatique", label: "Informatique / Tech" },
  { value: "formation", label: "Formation" },
  { value: "sante", label: "Santé / Bien-être" },
  { value: "immobilier", label: "Immobilier" },
  { value: "autre", label: "Autre" },
];

const DEVISES = [
  { value: "EUR", label: "EUR (€)", symbol: "€" },
  { value: "USD", label: "USD ($)", symbol: "$" },
  { value: "GBP", label: "GBP (£)", symbol: "£" },
  { value: "CHF", label: "CHF (Fr.)", symbol: "Fr." },
  { value: "XOF", label: "FCFA", symbol: "FCFA" },
];

const FORMATS_DATE = [
  { value: "DD/MM/YYYY", label: "JJ/MM/AAAA (31/12/2026)" },
  { value: "MM/DD/YYYY", label: "MM/JJ/AAAA (12/31/2026)" },
];

const SettingsPage = () => {
  const { user, token } = useAuth();
  const { updateLocalSettings } = useSettings();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);
  
  // État des paramètres
  const [settings, setSettings] = useState({
    user: { name: '' },
    business: {
      nom_entreprise: '',
      siret: '',
      date_debut_activite: '',
      secteur_activite: ''
    },
    preferences: {
      format_date: 'DD/MM/YYYY',
      devise: 'EUR',
      theme: 'light'
    }
  });

  // État du changement de mot de passe
  const [passwordData, setPasswordData] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Validation du mot de passe
  const passwordRules = {
    minLength: passwordData.new.length >= 8,
    hasUppercase: /[A-Z]/.test(passwordData.new),
    hasLowercase: /[a-z]/.test(passwordData.new),
    hasNumber: /[0-9]/.test(passwordData.new)
  };
  const passwordsMatch = passwordData.new && passwordData.confirm && passwordData.new === passwordData.confirm;

  // Charger les paramètres
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSettings({
          user: response.data.user || { name: user?.name || '' },
          business: response.data.business || {},
          preferences: response.data.preferences || { format_date: 'DD/MM/YYYY', devise: 'EUR', theme: 'light' }
        });
      } catch (error) {
        console.error('Erreur chargement paramètres:', error);
        // Utiliser les valeurs par défaut
        setSettings(prev => ({
          ...prev,
          user: { name: user?.name || '' }
        }));
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchSettings();
    }
  }, [token, user]);

  // Sauvegarder les paramètres
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Mettre à jour le contexte global pour synchroniser la devise
      updateLocalSettings(settings);
      toast.success('Paramètres enregistrés avec succès');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la sauvegarde';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // Changer le mot de passe
  const handleChangePassword = async () => {
    if (!passwordsMatch || Object.values(passwordRules).some(v => !v)) {
      toast.error('Veuillez corriger les erreurs');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API_URL}/api/settings/change-password`, {
        current_password: passwordData.current,
        new_password: passwordData.new
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mot de passe modifié avec succès');
      setShowPasswordModal(false);
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors du changement';
      toast.error(message);
    } finally {
      setChangingPassword(false);
    }
  };

  // Composant pour afficher une règle de validation
  const PasswordRule = ({ valid, text }) => (
    <div className={`flex items-center gap-2 text-xs ${valid ? 'text-emerald-600' : 'text-slate-400'}`}>
      {valid ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      <span>{text}</span>
    </div>
  );

  // Réinitialiser toutes les données
  const handleResetData = async () => {
    setResetting(true);
    try {
      const response = await axios.delete(`${API_URL}/api/reset-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Données supprimées : ${response.data.deleted.recettes} recettes, ${response.data.deleted.depenses} dépenses`);
      setShowResetDialog(false);
      // Recharger la page pour refléter les changements
      window.location.reload();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(message);
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-chivo text-2xl font-bold text-slate-900">Paramètres</h1>
        <p className="text-slate-500 mt-1">Gérez vos informations personnelles et préférences</p>
      </div>

      {/* Section 1: Informations personnelles */}
      <Card data-testid="personal-info-section">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Informations personnelles</CardTitle>
              <CardDescription>Vos informations de compte</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom complet</Label>
              <Input
                id="name"
                value={settings.user.name}
                onChange={(e) => setSettings({
                  ...settings,
                  user: { ...settings.user, name: e.target.value }
                })}
                placeholder="Votre nom"
                data-testid="settings-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={user?.email || ''}
                disabled
                className="bg-slate-50 text-slate-500"
                data-testid="settings-email-input"
              />
              <p className="text-xs text-slate-400">L'email ne peut pas être modifié</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={() => setShowPasswordModal(true)}
            className="mt-2"
            data-testid="change-password-btn"
          >
            <Lock className="h-4 w-4 mr-2" />
            Modifier le mot de passe
          </Button>
        </CardContent>
      </Card>

      {/* Section 2: Informations entreprise */}
      <Card data-testid="business-info-section">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Building2 className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Informations entreprise</CardTitle>
              <CardDescription>Les informations de votre auto-entreprise</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nom_entreprise">Nom de l'entreprise</Label>
              <Input
                id="nom_entreprise"
                value={settings.business.nom_entreprise || ''}
                onChange={(e) => setSettings({
                  ...settings,
                  business: { ...settings.business, nom_entreprise: e.target.value }
                })}
                placeholder="Ma Super Entreprise"
                data-testid="settings-business-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="siret">SIRET (optionnel)</Label>
              <Input
                id="siret"
                value={settings.business.siret || ''}
                onChange={(e) => setSettings({
                  ...settings,
                  business: { ...settings.business, siret: e.target.value }
                })}
                placeholder="123 456 789 00012"
                maxLength={17}
                data-testid="settings-siret-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date_debut">Date de début d'activité (optionnel)</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="date_debut"
                  type="date"
                  value={settings.business.date_debut_activite || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    business: { ...settings.business, date_debut_activite: e.target.value }
                  })}
                  className="pl-10"
                  data-testid="settings-date-debut-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="secteur">Secteur d'activité (optionnel)</Label>
              <Select
                value={settings.business.secteur_activite || ''}
                onValueChange={(value) => setSettings({
                  ...settings,
                  business: { ...settings.business, secteur_activite: value }
                })}
              >
                <SelectTrigger id="secteur" data-testid="settings-secteur-select">
                  <SelectValue placeholder="Sélectionner un secteur" />
                </SelectTrigger>
                <SelectContent>
                  {SECTEURS_ACTIVITE.map(secteur => (
                    <SelectItem key={secteur.value} value={secteur.value}>
                      {secteur.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Préférences */}
      <Card data-testid="preferences-section">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Settings2 className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Préférences</CardTitle>
              <CardDescription>Personnalisez l'affichage de l'application</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="format_date">Format de date</Label>
              <Select
                value={settings.preferences.format_date}
                onValueChange={(value) => setSettings({
                  ...settings,
                  preferences: { ...settings.preferences, format_date: value }
                })}
              >
                <SelectTrigger id="format_date" data-testid="settings-format-date-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FORMATS_DATE.map(format => (
                    <SelectItem key={format.value} value={format.value}>
                      {format.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="devise">Devise</Label>
              <Select
                value={settings.preferences.devise}
                onValueChange={(value) => setSettings({
                  ...settings,
                  preferences: { ...settings.preferences, devise: value }
                })}
              >
                <SelectTrigger id="devise" data-testid="settings-devise-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEVISES.map(devise => (
                    <SelectItem key={devise.value} value={devise.value}>
                      {devise.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Thème</Label>
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-700">Mode sombre</p>
                  <p className="text-xs text-slate-400">Cette fonctionnalité sera disponible prochainement</p>
                </div>
                <div className="px-3 py-1 bg-slate-200 text-slate-500 text-xs rounded-full">
                  Bientôt
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 4: Zone dangereuse */}
      <Card className="border-red-200" data-testid="danger-zone-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Zone dangereuse
          </CardTitle>
          <CardDescription>
            Actions irréversibles sur votre compte
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="font-medium text-red-900">Réinitialiser toutes mes données</h4>
                <p className="text-sm text-red-700 mt-1">
                  Supprime définitivement toutes vos recettes, dépenses et paramètres URSSAF. 
                  Cette action est irréversible.
                </p>
              </div>
              <Button 
                variant="destructive"
                onClick={() => setShowResetDialog(true)}
                className="flex-shrink-0"
                data-testid="reset-data-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Tout supprimer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bouton de sauvegarde */}
      <div className="flex justify-end pb-8">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Enregistrement...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Enregistrer les modifications
            </>
          )}
        </Button>
      </div>

      {/* Modal de changement de mot de passe */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier le mot de passe</DialogTitle>
            <DialogDescription>
              Entrez votre mot de passe actuel et choisissez un nouveau mot de passe sécurisé.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Mot de passe actuel</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordData.current}
                onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                placeholder="••••••••"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">Nouveau mot de passe</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordData.new}
                onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                placeholder="••••••••"
                data-testid="new-password-input"
              />
              {passwordData.new && (
                <div className="grid grid-cols-2 gap-1 pt-2">
                  <PasswordRule valid={passwordRules.minLength} text="8 caractères minimum" />
                  <PasswordRule valid={passwordRules.hasUppercase} text="1 majuscule" />
                  <PasswordRule valid={passwordRules.hasLowercase} text="1 minuscule" />
                  <PasswordRule valid={passwordRules.hasNumber} text="1 chiffre" />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirmer le nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordData.confirm}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                  placeholder="••••••••"
                  className={passwordData.confirm && (passwordsMatch ? 'border-emerald-500' : 'border-red-500')}
                  data-testid="confirm-new-password-input"
                />
                {passwordData.confirm && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {passwordsMatch ? (
                      <Check className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <X className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                )}
              </div>
              {passwordData.confirm && !passwordsMatch && (
                <p className="text-xs text-red-500">Les mots de passe ne correspondent pas</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleChangePassword}
              disabled={changingPassword || !passwordsMatch || Object.values(passwordRules).some(v => !v) || !passwordData.current}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-change-password-btn"
            >
              {changingPassword ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Modification...
                </>
              ) : (
                'Modifier'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogue de confirmation de réinitialisation */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirmer la suppression
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Êtes-vous sûr de vouloir supprimer <strong>toutes vos données</strong> ?
              </p>
              <p className="text-red-600 font-medium">
                Cette action supprimera définitivement :
              </p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>Toutes vos recettes</li>
                <li>Toutes vos dépenses</li>
                <li>Vos paramètres URSSAF</li>
                <li>Votre historique de déclarations</li>
              </ul>
              <p className="font-bold text-red-600 mt-2">
                Cette action est irréversible !
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetData}
              disabled={resetting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-reset-btn"
            >
              {resetting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Oui, tout supprimer
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SettingsPage;
